/**
 * shell/legal-page.ts
 *
 * Progressive enhancement controller for the public /terms page (Memphis
 * Legal Console design). Sibling of shell/faq-page.ts.
 *
 * Capabilities (all degrade gracefully without JS):
 *   - Reading-progress bar bound to scroll position.
 *   - Sticky-rail active-section tracking via IntersectionObserver.
 *   - Per-section copy-link buttons → writes to clipboard, shows toast.
 *   - FAQ filter chips that scope the accordion in place.
 *   - FAQ spotlight cards that open the matching <details> and scroll.
 *   - Hash-based open-on-load (`/terms#q-prohibited-uses`).
 *   - Honours `prefers-reduced-motion` for smooth scroll.
 *
 * Imports use `.js` even for `.ts` source files (per AGENTS §10).
 */
import { byId, query, queryAll } from '../types/dom.js';
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const TOAST_VISIBLE_MS = 1500;
const SPOTLIGHT_HIGHLIGHT_MS = 1200;
const ALL_CATEGORY = 'all';
function t(key, fallback) {
    const translate = window.tnT;
    return typeof translate === "function" ? translate(key, fallback) : fallback;
}
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
// ---------------------------------------------------------------------------
// Reading progress
// ---------------------------------------------------------------------------
function initReadingProgress(root) {
    let ticking = false;
    const update = () => {
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        const ratio = docHeight > 0
            ? Math.max(0, Math.min(1, window.scrollY / docHeight))
            : 0;
        root.style.setProperty('--legal-progress', `${(ratio * 100).toFixed(2)}%`);
        ticking = false;
    };
    const onScroll = () => {
        if (ticking)
            return;
        ticking = true;
        window.requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
}
function initRailTracking() {
    const links = queryAll('.legal-rail__link');
    if (links.length === 0)
        return;
    const linkByTarget = new Map();
    for (const element of links) {
        const href = element.getAttribute('href') ?? '';
        if (!href.startsWith('#'))
            continue;
        const targetId = href.slice(1);
        if (targetId.length === 0)
            continue;
        linkByTarget.set(targetId, { element, targetId });
    }
    const sections = [];
    for (const [id] of linkByTarget) {
        const el = document.getElementById(id);
        if (el !== null)
            sections.push(el);
    }
    if (sections.length === 0)
        return;
    const setActive = (id) => {
        for (const link of linkByTarget.values()) {
            const isActive = link.targetId === id;
            link.element.classList.toggle('is-active', isActive);
        }
    };
    if (typeof IntersectionObserver === 'undefined') {
        return;
    }
    const visible = new Map();
    const observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
            const id = entry.target.id;
            if (id.length === 0)
                continue;
            if (entry.isIntersecting) {
                visible.set(id, entry.intersectionRatio);
            }
            else {
                visible.delete(id);
            }
        }
        // Pick the section closest to the top of the viewport.
        let bestId = null;
        let bestTop = Infinity;
        for (const id of visible.keys()) {
            const el = document.getElementById(id);
            if (el === null)
                continue;
            const top = el.getBoundingClientRect().top;
            if (top < bestTop && top < window.innerHeight * 0.4) {
                bestTop = top;
                bestId = id;
            }
        }
        setActive(bestId);
    }, {
        rootMargin: '-20% 0px -60% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
    });
    for (const section of sections)
        observer.observe(section);
}
function createToast() {
    const root = query('.legal-toast');
    const text = query('.legal-toast__text');
    let hideTimer = null;
    return {
        show(message) {
            if (root === null)
                return;
            // Un-hide BEFORE writing the message. `.legal-toast` is the live region
            // (role="status" aria-live="polite") and the [hidden] attribute takes it
            // out of the accessibility tree entirely, so a textContent write made
            // while it was still hidden mutated a subtree no screen reader was
            // observing -- and the un-hide that followed carried no mutation of its
            // own. The result was that the copy-link confirmation, the only feedback
            // these icon-only buttons produce, was never announced. The two lines
            // are in this order for that reason; do not swap them back.
            root.hidden = false;
            if (text !== null)
                text.textContent = message;
            // Force a layout flush so the transition runs.
            void root.offsetHeight;
            root.classList.add('is-visible');
            if (hideTimer !== null)
                window.clearTimeout(hideTimer);
            hideTimer = window.setTimeout(() => {
                root.classList.remove('is-visible');
                window.setTimeout(() => {
                    root.hidden = true;
                    // Clear on hide so the next copy of the SAME section is a real
                    // text change. Re-writing an identical string into a live region
                    // is not a mutation, and the second press would go unannounced.
                    if (text !== null)
                        text.textContent = '';
                }, 200);
            }, TOAST_VISIBLE_MS);
        },
    };
}
// ---------------------------------------------------------------------------
// Copy-link buttons
// ---------------------------------------------------------------------------
function initCopyLinks(toast) {
    queryAll('[data-legal-copy]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const id = btn.dataset['legalCopy'];
            if (id === undefined || id.length === 0)
                return;
            const url = `${window.location.origin}${window.location.pathname}#${id}`;
            const writePromise = navigator.clipboard?.writeText(url);
            if (writePromise !== undefined) {
                writePromise.then(() => toast.show(t('js.legal.linkCopied', 'Section link copied')), () => toast.show(t('js.legal.copyFailed', 'Could not copy link')));
            }
            else {
                const tmp = document.createElement('input');
                tmp.value = url;
                document.body.appendChild(tmp);
                tmp.select();
                try {
                    document.execCommand('copy');
                    toast.show(t('js.legal.linkCopied', 'Section link copied'));
                }
                catch {
                    toast.show(t('js.legal.copyFailed', 'Could not copy link'));
                }
                document.body.removeChild(tmp);
            }
        });
    });
}
// ---------------------------------------------------------------------------
// FAQ — filter chips + accordion + spotlight + hash-open
// ---------------------------------------------------------------------------
function applyFilter(items, category) {
    for (const item of items) {
        const cat = item.dataset['category'] ?? '';
        const visible = category === ALL_CATEGORY || cat === category;
        item.classList.toggle('is-hidden', !visible);
        if (!visible)
            item.open = false;
    }
}
function initFaqFilters(items) {
    const chips = queryAll('[data-legal-faq-filter]');
    if (chips.length === 0)
        return;
    chips.forEach((chip) => {
        chip.addEventListener('click', () => {
            const next = chip.dataset['legalFaqFilter'] ?? ALL_CATEGORY;
            chips.forEach((c) => {
                const isActive = c === chip;
                c.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            });
            applyFilter(items, next);
        });
    });
}
function closeSiblings(items, openItem) {
    for (const item of items) {
        if (item !== openItem && item.open)
            item.open = false;
    }
}
function initFaqAccordion(items) {
    for (const item of items) {
        item.addEventListener('toggle', () => {
            if (item.open)
                closeSiblings(items, item);
        });
    }
}
function openItemById(itemId, items, withHighlight) {
    const target = document.getElementById(itemId);
    if (target === null)
        return;
    if (!(target instanceof HTMLDetailsElement))
        return;
    closeSiblings(items, target);
    target.open = true;
    target.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start',
    });
    if (withHighlight) {
        target.classList.add('is-spotlighted');
        window.setTimeout(() => {
            target.classList.remove('is-spotlighted');
        }, SPOTLIGHT_HIGHLIGHT_MS);
    }
}
function initFaqSpotlight(items) {
    queryAll('[data-legal-spotlight-target]').forEach((card) => {
        card.addEventListener('click', (event) => {
            const targetId = card.dataset['legalSpotlightTarget'];
            if (targetId === undefined || targetId.length === 0)
                return;
            // Allow modifier-clicks to pass through (open in new tab, etc.).
            if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
                return;
            }
            event.preventDefault();
            window.history.replaceState(null, '', `#${targetId}`);
            openItemById(targetId, items, true);
        });
    });
}
function openFaqFromHashOnLoad(items) {
    const hash = window.location.hash.slice(1);
    if (hash.length === 0)
        return;
    if (!hash.startsWith('q-'))
        return;
    window.requestAnimationFrame(() => {
        openItemById(hash, items, true);
    });
}
// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------
function init() {
    const root = byId('legalPage');
    if (root === null)
        return;
    initReadingProgress(root);
    initRailTracking();
    const toast = createToast();
    initCopyLinks(toast);
    const items = queryAll('.legal-faq__item');
    if (items.length > 0) {
        initFaqFilters(items);
        initFaqAccordion(items);
        initFaqSpotlight(items);
        openFaqFromHashOnLoad(items);
    }
}
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
}
else {
    init();
}
//# sourceMappingURL=legal-page.js.map